import React, { useState, useEffect } from 'react';
import { sponsorBannersService } from '../../../services/supabaseService';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const SponsorBanner = ({ language }) => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBanners();

    // Listen for banner updates from admin panel via custom events
    const handleBannerUpdate = () => {
      loadBanners();
    };

    window.addEventListener('bannerUpdated', handleBannerUpdate);

    // Also listen for localStorage changes as fallback
    const handleStorageChange = (e) => {
      if (e.key === 'bannersUpdated') {
        loadBanners();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Check for updates every 10 seconds (reduced from 30)
    const refreshInterval = setInterval(() => {
      loadBanners();
    }, 10000);

    return () => {
      window.removeEventListener('bannerUpdated', handleBannerUpdate);
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(refreshInterval);
    };
  }, []);

  useEffect(() => {
    if (banners?.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % banners?.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [banners?.length]);

  const loadBanners = async () => {
    try {
      // Add timestamp to force fresh data from server
      const timestamp = Date.now();
      console.log('Loading sponsor banners at:', timestamp);
      const data = await sponsorBannersService?.getActive();
      console.log('Loaded banners:', data);
      setBanners(data);
    } catch (error) {
      console.error('Failed to load sponsor banners:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || banners?.length === 0) return null;

  const currentBanner = banners?.[currentIndex];

  return (
    <div className="bg-gradient-to-r from-secondary/10 to-accent/10 rounded-lg border border-secondary/20 p-6 mb-8 animate-fade-in shadow-lg hover:shadow-xl transition-smooth">
      <div className="flex items-center gap-2 mb-3">
        <Icon name="Award" size={20} className="coffee-text-dark" />
        <h3 className="text-sm font-semibold coffee-text-dark">
          {language === 'en' ? 'Platinum Sponsor' : '铂金赞助商'}
        </h3>
      </div>
      <div className="relative overflow-hidden rounded-lg bg-card shadow-md">
        {currentBanner?.mediaType === 'video' ? (
          <video
            src={currentBanner?.mediaUrl}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-48 object-cover"
          />
        ) : (
          <a 
            href={currentBanner?.linkUrl || '#'} 
            target="_blank" 
            rel="noopener noreferrer"
            className="block"
          >
            <Image
              src={currentBanner?.mediaUrl}
              alt={language === 'en' ? currentBanner?.titleEn : currentBanner?.titleZh}
              className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
            />
          </a>
        )}
        {banners?.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
            {banners?.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-secondary w-6' : 'bg-card/60 w-2'
                }`}
                aria-label={`Go to banner ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
      <p className="text-sm coffee-text-medium mt-2 text-center font-medium">
        {language === 'en' ? currentBanner?.titleEn : currentBanner?.titleZh}
      </p>
    </div>
  );
};

export default SponsorBanner;